import React from "react";
import { Button, Icon } from "../dm";
import { classnames, withCacheBuster } from "../../utils";
import * as styles from "./file-preview.module.scss";

interface IFilePreviewProps {
  selectedFile?: File | undefined;
  disabled?: boolean;
  src?: string | undefined;
  displayName?: string;
  onFileDelete?: () => void;
  onFileView?: (file: File) => void;
  thumbnailSize?: "s" | "m";
}

const getFileNameFromUrl = (url: string): string => {
  const [path] = url.split("?");
  return decodeURIComponent(path.split("/").pop() || "");
};

const getFileType = (fileName: string): "image" | "video" | "document" => {
  const suffix = fileName.split(".").pop() || "";
  if (["jpg", "jpeg", "png", "bmp", "gif", "svg"].includes(suffix)) {
    return "image";
  }
  if (["mov", "mp4", "mpeg", "avi"].includes(suffix)) {
    return "video";
  }
  return "document";
};

const FilePreview = ({
  selectedFile,
  src,
  displayName,
  disabled,
  onFileDelete,
  onFileView,
  thumbnailSize = "s",
}: IFilePreviewProps) => {
  const [previewSrc, setPreviewSrc] = React.useState<string | undefined>();

  React.useEffect(() => {
    if (selectedFile) {
      const objectUrl = URL.createObjectURL(selectedFile);
      setPreviewSrc(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }

    setPreviewSrc(withCacheBuster(src));
    return undefined;
  }, [selectedFile, src]);

  if (!previewSrc) {
    return null;
  }
  const imgName = selectedFile?.name || displayName || (src ? getFileNameFromUrl(src) : getFileNameFromUrl(previewSrc));
  const fileType = getFileType(imgName || "");

  const handleClick = () => {
    if (selectedFile && onFileView) {
      onFileView(selectedFile);
    }
  };

  const renderPreview = () => (
    <span className={styles.preview}>
      {fileType === "image" && (
        <img
          src={previewSrc}
          alt={selectedFile ? selectedFile.name : ""}
          className={classnames({
            [styles.image]: true,
            [styles[thumbnailSize]]: !!styles[thumbnailSize],
          })}
        />
      )}
      {fileType === "video" && (
        <video
          src={previewSrc}
          className={classnames({
            [styles.image]: true,
            [styles[thumbnailSize]]: !!styles[thumbnailSize],
          })}
          muted
          preload="metadata"
        />
      )}
      {fileType === "document" && <Icon name="documents" />}
      <span>{imgName}</span>
    </span>
  );

  return (
    <div className={classnames({ [styles.container]: true, [styles.disabled]: !!disabled })}>
      {selectedFile && (
        <button type="button" className={styles.link} disabled={disabled} onClick={handleClick}>
          {renderPreview()}
        </button>
      )}
      {src && !disabled && (
        <a href={previewSrc} target="_blank" className={styles.link} rel="noreferrer">
          {renderPreview()}
        </a>
      )}
      {src && disabled && <span className={styles.disabled}>{renderPreview()}</span>}
      {onFileDelete && (
        <Button
          type="button"
          icon="trash"
          kind="invisible"
          disabled={disabled}
          aria-label="Delete file"
          onClick={onFileDelete}
        />
      )}
    </div>
  );
};

export default FilePreview;
